import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Megaphone } from 'lucide-react';
import { api } from '@/lib/api';

type Announcement = Awaited<ReturnType<typeof api.announcements.unseen>>[number];

interface Props {
  userId: string; // re-fetch when user changes
}

export function AnnouncementModal({ userId }: Props) {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!userId) return;
    api.announcements.unseen()
      .then((list) => {
        setAnnouncements(list);
        if (list.length > 0) setOpen(true);
      })
      .catch(console.error);
  }, [userId]);

  function handleAcknowledge() {
    api.announcements.markSeen().catch(console.error);
    setOpen(false);
    setAnnouncements([]);
  }

  function handleLater() {
    setOpen(false);
  }

  if (announcements.length === 0) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleLater()}>
      <DialogContent className="max-h-[80dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Megaphone size={18} />
            {announcements.length === 1 ? 'Was ist neu' : 'Was ist neu bei uns'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5 mt-4">
          {announcements.map((a) => (
            <article key={a.id} className="space-y-2">
              <div>
                <h3 className="font-semibold text-sm">{a.title}</h3>
                <p className="text-xs text-muted-foreground">
                  {new Date(a.publishedAt).toLocaleDateString('de-DE', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                  })}
                </p>
              </div>
              <p className="text-sm text-foreground/90 whitespace-pre-line leading-relaxed">
                {a.body}
              </p>
              {a.linkHref && a.linkLabel && (
                a.linkHref.startsWith('/') ? (
                  <Link
                    to={a.linkHref}
                    onClick={handleAcknowledge}
                    className="inline-block text-sm text-primary underline underline-offset-2"
                  >
                    {a.linkLabel} →
                  </Link>
                ) : (
                  <a
                    href={a.linkHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={handleAcknowledge}
                    className="inline-block text-sm text-primary underline underline-offset-2"
                  >
                    {a.linkLabel} →
                  </a>
                )
              )}
            </article>
          ))}
        </div>

        <div className="flex gap-2 justify-end mt-6">
          <Button variant="outline" onClick={handleLater}>Später</Button>
          <Button onClick={handleAcknowledge}>Verstanden</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
